/*
Copyright (c) 2012-2023 Grab Taxi Holdings PTE LTD (GRAB), All Rights Reserved.
NOTICE: All information contained herein is, and remains the property of GRAB.
The intellectual and technical concepts contained herein are confidential,
proprietary and controlled by GRAB and may be covered by patents, patents in process,
and are protected by trade secret or copyright law.

You are strictly forbidden to copy, download, store (in any medium), transmit, disseminate,
adapt or change this material in any way unless prior written permission is obtained from GRAB.
Access to the source code contained herein is hereby forbidden to anyone except current GRAB
employees or contractors with binding Confidentiality and Non-disclosure agreements explicitly
covering such access.

The copyright notice above does not evidence any actual or intended publication or disclosure of
this source code, which includes information that is confidential and/or proprietary,
and is a trade secret, of GRAB.

ANY REPRODUCTION, MODIFICATION, DISTRIBUTION, PUBLIC PERFORMANCE, OR PUBLIC DISPLAY OF OR
THROUGH USE OF THIS SOURCE CODE WITHOUT THE EXPRESS WRITTEN CONSENT OF GRAB IS STRICTLY PROHIBITED,
AND IN VIOLATION OF APPLICABLE LAWS AND INTERNATIONAL TREATIES. THE RECEIPT OR POSSESSION OF THIS
SOURCE CODE AND/OR RELATED INFORMATION DOES NOT CONVEY OR IMPLY ANY RIGHTS TO REPRODUCE,
DISCLOSE OR DISTRIBUTE ITS CONTENTS, OR TO MANUFACTURE, USE, OR SELL ANYTHING THAT IT MAY DESCRIBE,
IN WHOLE OR IN PART.
*/
/** @jsx jsx */
import { css, jsx } from "@emotion/core"
import { ChangeEvent, useCallback, useContext, useEffect, useRef } from "react"
import {
  BULK_UPLOAD_TYPE,
  BulkUploadContext,
  DRAWER_STATES,
} from "pages/menu/detail/bulkUpload/BulkUploadContext"
import {
  INVENTORY_BULK_UPLOAD_SUPPORTED_FILES,
  MENU_BULK_UPLOAD_SUPPORTED_FILES,
} from "constants/menu_bulk_upload"
import FileWidget from "./FileWidget"

const INPUT_ID = "INPUT_ID"

type PropTypes = {
  selectedFileName: string
  onSelectFileFromLocal: (e: ChangeEvent<HTMLInputElement>) => void
  onDropFileFromLocal: (e: DragEvent) => void
}

const checkIfDropFileInAllowedArea = (
  e: DragEvent,
  dropableDiv: HTMLDivElement | null,
): boolean => {
  if (!e || !dropableDiv) return false

  const dropableDivBound = dropableDiv.getBoundingClientRect()
  if (!dropableDivBound) return false

  // this check will go wrong if using Chrome Device tool and adjust the zoom level, mouse event clientX/Y don't adapt to the scale
  // but normal zoom in/out feature (change in browser setting or Cmd key and “+” or “-” combos) would not break this
  return (
    e.clientX > dropableDivBound.left &&
    e.clientX < dropableDivBound.right &&
    e.clientY > dropableDivBound.top &&
    e.clientY < dropableDivBound.bottom
  )
}

const FileDragNDrop = ({
  onSelectFileFromLocal,
  selectedFileName,
  onDropFileFromLocal,
}: PropTypes) => {
  const { bulkUploadType, drawerState, drawerVisible } = useContext(
    BulkUploadContext,
  )

  const supportedFiles =
    bulkUploadType === BULK_UPLOAD_TYPE.INVENTORY
      ? INVENTORY_BULK_UPLOAD_SUPPORTED_FILES
      : MENU_BULK_UPLOAD_SUPPORTED_FILES

  const supportedFilesAccept = supportedFiles
    .map(supportedFile => `.${supportedFile}`)
    .join(", ")

  const divDragNDropRef = useRef<HTMLDivElement>(null)

  const onDragoverFile = useCallback((e: DragEvent) => {
    e.preventDefault() // prevent browser popup another window to download file
  }, [])

  const onDropFile = useCallback(
    (e: DragEvent) => {
      e.preventDefault() // prevent browser popup another window to download file
      if (checkIfDropFileInAllowedArea(e, divDragNDropRef?.current)) {
        onDropFileFromLocal(e)
      }
    },
    [onDropFileFromLocal],
  )

  useEffect(() => {
    if (
      (drawerVisible && drawerState === DRAWER_STATES.START) ||
      drawerState === DRAWER_STATES.PUBLISHABLE
    ) {
      // subscribe to element drop event does not work, because drop event will be triggered at ant-drawer-mask component (full-screen drawer layout) first
      // hence need to subscribe at window level, then detect the drop area
      window.addEventListener("drop", onDropFile)
      window.addEventListener("dragover", onDragoverFile)

      return () => {
        window.removeEventListener("drop", onDropFile)
        window.removeEventListener("dragover", onDragoverFile)
      }
    }

    return () => {}
  }, [drawerVisible, drawerState, onDropFile, onDragoverFile])

  return (
    <div>
      <input
        type="file"
        accept={supportedFilesAccept}
        id={INPUT_ID}
        onChange={onSelectFileFromLocal}
        css={css`
          display: none;
        `}
      />
      <label htmlFor={INPUT_ID}>
        <div ref={divDragNDropRef}>
          <FileWidget selectedFileName={selectedFileName} />
        </div>
      </label>
    </div>
  )
}

export default FileDragNDrop
